// Completely rewritten AuthContext.js to eliminate circular dependencies
// Enhanced with token expiration management

import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import authService from '../services/authService';
import tokenManager from '../utils/tokenManager';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ADDED: Token expiration event listeners
  useEffect(() => {
    const handleTokenExpired = (event) => {
      console.log('Token expired event received:', event.detail);
      setUser(null);
      setError('Session expired');
      
      // Force logout without API call since token is expired
      tokenManager.clearTokens();
    };

    const handleStorageChange = (e) => {
      // Handle token changes in other tabs
      if (e.key === 'access_token' && !e.newValue && e.oldValue) {
        console.log('Token removed in another tab');
        setUser(null);
        setError('Session expired in another tab');
      }
    };

    // Add event listeners
    window.addEventListener('tokenExpired', handleTokenExpired);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('tokenExpired', handleTokenExpired);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      try {
        if (authService.isAuthenticated()) {
          const storedUser = authService.getStoredUser();
          
          if (storedUser) {
            const enhancedUser = enhanceUserData(storedUser);
            setUser(enhancedUser);
            
            // ADDED: Setup auto-logout for existing session
            authService.setupAutoLogout();
            
            try {
              const currentUserData = await authService.getCurrentUser();
              console.log('Auth init - refreshed user data:', currentUserData);
              
              const { data } = currentUserData;
              const refreshedUser = data.user || data;
              const enhancedRefreshedUser = enhanceUserData(refreshedUser);
              
              setUser(enhancedRefreshedUser);
            } catch (refreshError) {
              console.warn('Could not refresh user data:', refreshError);
              // Keep the enhanced stored user
            }
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // Simple, safe user data enhancement without circular dependencies
  const enhanceUserData = (userData) => {
    if (!userData) return null;
    
    console.log('Enhancing user data:', userData);
    
    // Safely extract roles
    let roles = [];
    if (Array.isArray(userData.roles)) {
      roles = userData.roles.map(role => {
        if (typeof role === 'string') return role;
        if (role && typeof role === 'object') return role.name || role.display_name || '';
        return '';
      }).filter(Boolean);
    }
    
    // Safely extract permissions
    let permissions = [];
    
    // Method 1: Direct permissions array
    if (Array.isArray(userData.permissions)) {
      const directPermissions = userData.permissions.map(perm => {
        if (typeof perm === 'string') return perm;
        if (perm && typeof perm === 'object') return perm.name || '';
        return '';
      }).filter(Boolean);
      permissions = [...permissions, ...directPermissions];
    }
    
    // Method 2: Permissions from roles (if available)
    if (Array.isArray(userData.roles)) {
      userData.roles.forEach(role => {
        if (role && role.permissions && Array.isArray(role.permissions)) {
          const rolePermissions = role.permissions.map(perm => {
            if (typeof perm === 'string') return perm;
            if (perm && typeof perm === 'object') return perm.name || '';
            return '';
          }).filter(Boolean);
          permissions = [...permissions, ...rolePermissions];
        }
      });
    }
    
    // Method 3: Add staff category permissions automatically
    const isStaffUser = userData.is_agent === true || 
                       userData.is_agent === 1 || 
                       (userData.staff_level && userData.staff_level > 0) ||
                       roles.some(role => 
                         role.toLowerCase().includes('staff') ||
                         role.toLowerCase().includes('agent')
                       );
    
    if (isStaffUser) {
      console.log('Adding staff category permissions for:', userData.name);
      permissions.push('categories.view', 'categories.view-tree');
    }
    
    // Method 4: Handle ticket permissions from user_permissions object
    if (userData.user_permissions && typeof userData.user_permissions === 'object') {
      const ticketPermissionMap = {
        'view_all': 'tickets.view-all',
        'view_assigned': 'tickets.view-assigned', 
        'view_own': 'tickets.view-own',
        'create': 'tickets.create',
        'update': 'tickets.update',
        'assign': 'tickets.assign',
        'escalate': 'tickets.escalate',
        'forward': 'tickets.forward',
        'solve': 'tickets.solve',
        'close': 'tickets.close',
        'comment': 'tickets.comment',
        'respond': 'tickets.respond'
      };
      
      Object.keys(userData.user_permissions).forEach(key => {
        if (userData.user_permissions[key] === true && ticketPermissionMap[key]) {
          permissions.push(ticketPermissionMap[key]);
        }
      });
    }
    
    // Remove duplicates and filter out empty values
    permissions = [...new Set(permissions.filter(Boolean))];
    
    console.log('Final extracted roles:', roles);
    console.log('Final extracted permissions:', permissions);
    
    return {
      ...userData,
      roles: roles,
      permissions: permissions,
      user_permissions: userData.user_permissions
    };
  };

  // UPDATED: Enhanced login with auto-logout setup
  const login = useCallback(async (email, password) => {
    try {
      setLoading(true);
      setError(null);
      
      const responseData = await authService.login(email, password);
      const { data } = responseData;
      const rawUser = data.user || data;
      const enhancedUser = enhanceUserData(rawUser);
      
      setUser(enhancedUser);
      
      // ADDED: Setup auto-logout after successful login
      authService.setupAutoLogout();
      
      return responseData;
    } catch (error) {
      console.error('Login failed:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // UPDATED: Enhanced register with auto-logout setup
  const register = useCallback(async (userData) => {
    try {
      setLoading(true);
      setError(null);
      
      const responseData = await authService.register(userData);
      const { data } = responseData;
      const rawUser = data.user || data;
      const enhancedUser = enhanceUserData(rawUser);
      
      setUser(enhancedUser);
      
      // ADDED: Setup auto-logout after successful registration
      authService.setupAutoLogout();
      
      return responseData;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      setLoading(true);
      await authService.logout();
      setUser(null);
      setError(null); // Clear any existing errors on logout
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateProfile = useCallback(async (profileData) => {
    try {
      setLoading(true);
      setError(null);
      
      const responseData = await authService.updateProfile(profileData);
      const { data } = responseData;
      const rawUser = data.user || data;
      const enhancedUser = enhanceUserData(rawUser);
      
      setUser(enhancedUser);
      return responseData;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const changePassword = useCallback(async (currentPassword, newPassword, confirmPassword) => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await authService.changePassword(currentPassword, newPassword, confirmPassword);
      return data;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const uploadProfileImage = useCallback(async (file) => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await authService.uploadProfileImage(file);
      
      setUser(prev => ({
        ...prev,
        profile_image: data.profile_image_url
      }));
      
      return data;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Helper function to get user permissions safely
  const getUserPermissions = useCallback(() => {
    if (!user || !Array.isArray(user.permissions)) return [];
    return user.permissions;
  }, [user]);

  // Helper function to get user roles safely
  const getUserRoles = useCallback(() => {
    if (!user || !Array.isArray(user.roles)) return [];
    return user.roles;
  }, [user]);

  // Simple permission check - no circular dependencies
  const hasPermission = useCallback((permission) => {
    if (!user || !permission) return false;
    
    const userPermissions = getUserPermissions();
    
    // Admin check - look for admin permissions directly
    const adminPermissions = ['users.delete', 'roles.create', 'roles.delete', 'manage-system'];
    const isUserAdmin = adminPermissions.some(perm => userPermissions.includes(perm)) ||
                       getUserRoles().some(role => role.toLowerCase() === 'admin' || role.toLowerCase() === 'administrator');
    
    if (isUserAdmin) {
      console.log('Permission granted to admin:', permission);
      return true;
    }
    
    // Staff bypass for categories
    const isStaffUser = user.is_agent === true || 
                       user.is_agent === 1 || 
                       user.staff_level > 0 ||
                       getUserRoles().some(role => 
                         role.toLowerCase().includes('staff') ||
                         role.toLowerCase().includes('agent')
                       );
    
    if (isStaffUser && permission.startsWith('categories.')) {
      console.log('Permission granted to staff for categories:', permission);
      return true;
    }
    
    // Direct permission check
    const hasDirectPermission = userPermissions.includes(permission);
    
    // Fallback check for ticket permissions via user_permissions object
    let hasTicketPermission = false;
    if (!hasDirectPermission && permission.startsWith('tickets.') && user.user_permissions) {
      const ticketPermissionKey = permission.replace('tickets.', '').replace('-', '_');
      hasTicketPermission = user.user_permissions[ticketPermissionKey] === true;
    }
    
    const result = hasDirectPermission || hasTicketPermission;
    
    if (process.env.NODE_ENV === 'development') {
      console.log('Permission check:', { 
        permission, 
        hasDirectPermission,
        hasTicketPermission,
        result,
        isAdmin: isUserAdmin,
        isStaff: isStaffUser
      });
    }
    
    return result;
  }, [user, getUserPermissions, getUserRoles]);

  const hasAnyPermission = useCallback((permissions) => {
    if (!permissions || !Array.isArray(permissions)) return false;
    return permissions.some(permission => hasPermission(permission));
  }, [hasPermission]);

  const hasRole = useCallback((roleName) => {
    if (!user || !roleName) return false;
    
    const userRoles = getUserRoles();
    const result = userRoles.some(role => role.toLowerCase() === roleName.toLowerCase());
    
    if (process.env.NODE_ENV === 'development') {
      console.log('Role check:', { roleName, userRoles, result });
    }
    
    return result;
  }, [user, getUserRoles]);

  const hasAnyRole = useCallback((roles) => {
    if (!roles || !Array.isArray(roles)) return false;
    return roles.some(role => hasRole(role));
  }, [hasRole]);

  // Simple role checks without circular dependencies
  const isAdmin = useCallback(() => {
    if (!user) return false;
    
    const userPermissions = getUserPermissions();
    const userRoles = getUserRoles();
    
    const adminPermissions = ['users.delete', 'roles.create', 'roles.delete', 'manage-system'];
    const hasAdminPerms = adminPermissions.some(perm => userPermissions.includes(perm));
    const hasAdminRole = userRoles.some(role => role.toLowerCase() === 'admin' || role.toLowerCase() === 'administrator');
    
    return hasAdminPerms || hasAdminRole;
  }, [user, getUserPermissions, getUserRoles]);

  const isAgent = useCallback(() => {
    if (!user) return false;
    
    const isAgentFlag = user.is_agent === true || user.is_agent === 1;
    const hasStaffLevel = user.staff_level && user.staff_level > 0;
    const userRoles = getUserRoles();
    const hasAgentRole = userRoles.some(role => 
      role.toLowerCase().includes('agent') ||
      role.toLowerCase().includes('staff')
    );
    
    const result = isAgentFlag || hasStaffLevel || hasAgentRole;
    
    if (process.env.NODE_ENV === 'development') {
      console.log('Agent check:', { isAgentFlag, hasStaffLevel, hasAgentRole, result });
    }
    
    return result;
  }, [user, getUserRoles]);

  const isCustomerCare = useCallback(() => {
    if (!user) return false;
    
    const userPermissions = getUserPermissions();
    const userRoles = getUserRoles();
    
    const customerCarePermissions = ['customers.search', 'customers.contact', 'tickets.create-behalf'];
    const hasCustomerCarePerms = customerCarePermissions.some(perm => userPermissions.includes(perm));
    const hasCustomerCareRole = userRoles.some(role => 
      role.toLowerCase().includes('care') ||
      role.toLowerCase().includes('support')
    );
    
    return hasCustomerCarePerms || hasCustomerCareRole;
  }, [user, getUserPermissions, getUserRoles]);

  const isCustomer = useCallback(() => {
    return !isAgent();
  }, [isAgent]);

  const getStaffLevel = useCallback(() => {
    return user?.staff_level || 0;
  }, [user]);

  const canCreateOnBehalf = useCallback(() => {
    if (!user) return false;
    
    const isStaffUser = user.is_agent === true || user.is_agent === 1 || user.staff_level > 0;
    if (!isStaffUser) return false;
    
    const userPermissions = getUserPermissions();
    return userPermissions.includes('tickets.create-behalf');
  }, [user, getUserPermissions]);

  const canManageTickets = useCallback(() => {
    if (!user) return false;
    
    const userPermissions = getUserPermissions();
    const ticketManagementPermissions = [
      'tickets.assign', 'tickets.escalate', 'tickets.forward',
      'tickets.solve', 'tickets.close', 'tickets.view-all'
    ];
    
    return ticketManagementPermissions.some(perm => userPermissions.includes(perm));
  }, [user, getUserPermissions]);

  const canManageUsers = useCallback(() => {
    if (!user) return false;
    
    const userPermissions = getUserPermissions();
    const userManagementPermissions = ['users.create', 'users.update', 'users.delete', 'users.view'];
    
    return userManagementPermissions.some(perm => userPermissions.includes(perm));
  }, [user, getUserPermissions]);

  const canManageSystem = useCallback(() => {
    if (!user) return false;
    
    const userPermissions = getUserPermissions();
    const systemPermissions = ['manage-system', 'config.update', 'roles.create', 'roles.update', 'roles.delete'];
    
    return systemPermissions.some(perm => userPermissions.includes(perm));
  }, [user, getUserPermissions]);

  const getUserPrimaryRole = useCallback(() => {
    if (!user) return 'Guest';
    
    if (isAdmin()) return 'Administrator';
    if (isCustomerCare()) return 'Customer Care';
    if (isAgent()) return `Level ${getStaffLevel()} Staff`;
    
    return 'Customer';
  }, [user, isAdmin, isCustomerCare, isAgent, getStaffLevel]);

  const getUserDisplayName = useCallback(() => {
    if (!user) return 'Guest';
    return user.name || user.email || 'User';
  }, [user]);

  const getUserAvatar = useCallback(() => {
    if (user?.profile_image) {
      return { type: 'image', value: user.profile_image };
    }
    
    const name = getUserDisplayName();
    const initials = name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
    
    return { type: 'initials', value: initials };
  }, [user, getUserDisplayName]);

  // ADDED: Get token expiration info for debugging
  const getTokenInfo = useCallback(() => {
    if (process.env.NODE_ENV !== 'development') return null;
    
    return tokenManager.getTokenInfo();
  }, []);

  const getDebugInfo = useCallback(() => {
    if (process.env.NODE_ENV !== 'development') return null;
    
    return {
      user: !!user,
      userId: user?.id,
      userName: user?.name,
      isAgent: isAgent(),
      isAdmin: isAdmin(), 
      isCustomerCare: isCustomerCare(),
      isCustomer: isCustomer(),
      canCreateOnBehalf: canCreateOnBehalf(),
      canManageTickets: canManageTickets(),
      canManageUsers: canManageUsers(),
      canManageSystem: canManageSystem(),
      staffLevel: getStaffLevel(),
      primaryRole: getUserPrimaryRole(),
      allRoles: getUserRoles(),
      permissionCount: getUserPermissions().length,
      samplePermissions: getUserPermissions().slice(0, 8),
      userFlags: {
        is_agent: user?.is_agent,
        staff_level: user?.staff_level,
        is_active: user?.is_active
      },
      testPermissions: {
        'categories.view': hasPermission('categories.view'),
        'tickets.view-all': hasPermission('tickets.view-all'),
        'users.create': hasPermission('users.create')
      },
      // ADDED: Token debugging info
      tokenInfo: getTokenInfo()
    };
  }, [
    user, isAgent, isAdmin, isCustomerCare, isCustomer, canCreateOnBehalf,
    canManageTickets, canManageUsers, canManageSystem, getStaffLevel,
    getUserPrimaryRole, getUserRoles, getUserPermissions, hasPermission, getTokenInfo
  ]);

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    
    // Auth methods
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    uploadProfileImage,
    
    // Core permission/role checking
    hasPermission,
    hasAnyPermission,
    hasRole,
    hasAnyRole,
    
    // Role helpers
    isAdmin,
    isAgent,
    isCustomerCare,
    isCustomer,
    
    // Feature-specific permissions
    canCreateOnBehalf,
    canManageTickets,
    canManageUsers, 
    canManageSystem,
    
    // User info helpers
    getUserDisplayName,
    getUserAvatar,
    getUserPrimaryRole,
    getStaffLevel,
    getUserPermissions,
    getUserRoles,
    
    // ADDED: Token management info
    getTokenInfo,
    
    // Debug (development only)
    getDebugInfo
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};